// Facebook Lead Ads integration — fresh build.
// Phase 1 MVP: webhook + Graph fetch + lead create + dedupe + retry queue.
// Setup is manual (paste tokens). OAuth, notifications, CAPI deferred.

import { useState, useEffect, useCallback, useRef } from "react";
import {
  Facebook, Loader2, AlertTriangle, CheckCircle, XCircle, Copy, Eye, EyeOff,
  ShieldCheck, ExternalLink, FileText, Activity, RefreshCw, Send, Clock,
  Settings2, Zap, RotateCw, BookOpen, LogIn, Lock, ChevronDown, ChevronUp, Link2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Visible callback URL — what admin pastes into Meta Developer Console.
// We display whatever the backend reports so localhost + prod both work.
const FALLBACK_WEBHOOK_PATH = "/api/integrations/facebook/webhook";

// eslint-disable-next-line no-unused-vars
export default function FacebookIntegration({ fbConfig, fbIntegrations, onRefresh }) {
  const { businessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [setup, setSetup] = useState(null);
  const [logs, setLogs] = useState(null);

  // Editable form state
  const [pageId, setPageId] = useState("");
  const [pageName, setPageName] = useState("");
  const [pageAccessToken, setPageAccessToken] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [formIdsCsv, setFormIdsCsv] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [autoAssign, setAutoAssign] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  // Field mapping: { <crm_field>: [...fb_question_names] }
  const [fieldMapping, setFieldMapping] = useState({});
  const [defaultMapping, setDefaultMapping] = useState({});
  const [savingMapping, setSavingMapping] = useState(false);
  const [questionsDialog, setQuestionsDialog] = useState({ open: false, formId: "", loading: false, data: null });

  // Test payload dialog
  const [testPayloadDialog, setTestPayloadDialog] = useState({ open: false });
  const [testPayloadInput, setTestPayloadInput] = useState("");
  const [testPayloadFormId, setTestPayloadFormId] = useState("");
  const [testPayloadRunning, setTestPayloadRunning] = useState(false);
  const [testPayloadResult, setTestPayloadResult] = useState(null);

  // Test connection
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Historical sync
  const [syncing, setSyncing] = useState(false);
  const [syncDays, setSyncDays] = useState("7");
  const [syncResult, setSyncResult] = useState(null);

  // ─── OAuth flow ─────────────────────────────────────────────────────────
  const [appConfig, setAppConfig] = useState(null);
  const [appIdInput, setAppIdInput] = useState("");
  const [appSecretInput, setAppSecretInput] = useState("");
  const [showAppSecret, setShowAppSecret] = useState(false);
  const [savingAppCfg, setSavingAppCfg] = useState(false);
  const [oauthInFlight, setOauthInFlight] = useState(false);
  const [oauthPages, setOauthPages] = useState(null); // list returned post-OAuth
  const [connectingPageId, setConnectingPageId] = useState(null);
  const [oauthError, setOauthError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const fetchAppConfig = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/app-config`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setAppConfig(d);
        setAppIdInput(d.app_id || "");
      }
    } catch (e) { console.error(e); }
  }, []);

  // True while an OAuth popup is open and hasn't reported back yet — used to
  // detect Facebook hard-blocks where the popup closes with no redirect.
  const popupClosedWithoutResult = useRef(false);

  // Listen for the popup's postMessage with the OAuth result
  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev?.data;
      if (!d || (d.type !== "facebook_oauth_success" && d.type !== "facebook_oauth_error")) return;
      popupClosedWithoutResult.current = false;
      setOauthInFlight(false);
      if (d.type === "facebook_oauth_error") {
        setOauthError(d.error || "OAuth failed");
        setOauthPages(null);
        toast.error(d.error || "Facebook connection failed");
      } else {
        setOauthError(null);
        setOauthPages(d.pages || []);
        toast.success(`Got ${d.pages?.length || 0} page(s) — pick one to connect`);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const handleSaveAppCfg = async () => {
    if (!appIdInput.trim()) { toast.error("App ID required"); return; }
    setSavingAppCfg(true);
    try {
      const body = { app_id: appIdInput.trim() };
      if (appSecretInput.trim()) body.app_secret = appSecretInput.trim();
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/app-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success("App credentials saved");
        setAppSecretInput("");
        await fetchAppConfig();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Save failed");
      }
    } catch (e) {
      toast.error("Save failed");
    } finally { setSavingAppCfg(false); }
  };

  const handleStartOAuth = async () => {
    if (!appConfig?.configured) { toast.error("Save App ID and Secret first"); return; }
    setOauthInFlight(true);
    setOauthError(null);
    setOauthPages(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/oauth-url${qs()}`, { credentials: "include" });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Failed to start OAuth");
        setOauthInFlight(false);
        return;
      }
      const d = await r.json();
      const w = 600, h = 750;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top  = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(d.authorization_url, "fb_oauth", `width=${w},height=${h},left=${left},top=${top}`);
      if (!popup) {
        toast.error("Popup blocked — allow popups for this site and try again");
        setOauthInFlight(false);
        return;
      }
      // Watch for the popup closing WITHOUT posting a result. That's what
      // happens when Facebook hard-blocks the login (e.g. "App not active" —
      // the FB app is in Development Mode and this FB account has no role on
      // it). Facebook never redirects to our callback in that case, so the
      // page would otherwise just spin forever with zero guidance.
      popupClosedWithoutResult.current = true; // reset by the postMessage handler
      const watcher = setInterval(() => {
        if (popup.closed) {
          clearInterval(watcher);
          setTimeout(() => {
            if (popupClosedWithoutResult.current) {
              setOauthInFlight(false);
              setOauthError("fb_access_blocked");
            }
          }, 400); // grace period for a late postMessage
        }
      }, 500);
    } catch (e) {
      toast.error("OAuth start failed");
      setOauthInFlight(false);
    }
  };

  const [disconnecting, setDisconnecting] = useState(false);
  const handleDisconnect = async () => {
    if (!window.confirm(`Disconnect "${setup?.page_name || setup?.page_id}" from Facebook? This stops lead capture for this page; you can reconnect any time.`)) return;
    setDisconnecting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/disconnect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ business_id: businessId }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(d.message || `Disconnected "${d.disconnected_page_name || ""}"`);
        setOauthPages(null);
        await fetchSetup();
      } else {
        toast.error(d.detail || "Disconnect failed");
      }
    } catch (e) {
      toast.error("Disconnect failed");
    } finally { setDisconnecting(false); }
  };

  const handleConnectPage = async (page) => {
    setConnectingPageId(page.id);
    try {
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/connect-page`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          business_id: businessId,
          page_id: page.id,
          page_name: page.name,
          page_access_token: page.access_token,
        }),
      });
      if (r.ok) {
        toast.success(`Connected page "${page.name}"`);
        setOauthPages(null);
        await fetchSetup();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Connect failed");
      }
    } catch (e) {
      toast.error("Connect failed");
    } finally { setConnectingPageId(null); }
  };

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    if (businessId) p.set("business_id", businessId);
    return p.toString() ? `?${p}` : "";
  }, [businessId]);

  const fetchSetup = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/setup${qs()}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setSetup(d);
        setPageId(d.page_id || "");
        setPageName(d.page_name || "");
        setFormIdsCsv((d.form_ids || []).join(", "));
        setEnabled(!!d.enabled);
        setAutoAssign(d.auto_assign_enabled !== false);
        setFieldMapping(d.field_mapping || {});
        setDefaultMapping(d.default_field_mapping || {});
        // Tokens are never echoed by backend — leave blank
        setPageAccessToken("");
        setAppSecret("");
      } else if (r.status === 400) {
        setSetup(null);
      } else {
        toast.error("Failed to load Facebook setup");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [qs]);

  const fetchLogs = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/logs${qs()}`, { credentials: "include" });
      if (r.ok) setLogs(await r.json());
    } catch (e) { console.error(e); }
  }, [qs]);

  useEffect(() => { fetchSetup(); fetchLogs(); fetchAppConfig(); }, [fetchSetup, fetchLogs, fetchAppConfig]);
  useEffect(() => {
    const t = setInterval(fetchLogs, 8000);
    return () => clearInterval(t);
  }, [fetchLogs]);

  const handleSave = async () => {
    if (!pageId.trim()) { toast.error("Page ID is required"); return; }
    setSaving(true);
    try {
      const body = {
        business_id: businessId,
        page_id: pageId.trim(),
        page_name: pageName.trim(),
        form_ids: formIdsCsv.split(",").map(s => s.trim()).filter(Boolean),
        enabled,
        auto_assign_enabled: autoAssign,
      };
      if (pageAccessToken.trim()) body.page_access_token = pageAccessToken.trim();
      if (appSecret.trim())       body.app_secret = appSecret.trim();
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/setup`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success("Saved");
        setPageAccessToken("");
        setAppSecret("");
        await fetchSetup();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Save failed");
      }
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/test-connection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ business_id: businessId }),
      });
      const d = await r.json().catch(() => ({}));
      setTestResult({ ok: r.ok, data: d });
      if (r.ok) toast.success(`Page reachable — ${d.forms?.length || 0} lead form(s) found`);
      else toast.error(d.detail || "Test failed");
    } catch (e) {
      setTestResult({ ok: false, data: { error: e.message } });
      toast.error("Test failed");
    } finally {
      setTesting(false);
    }
  };

  // ──────── Field Mapping helpers ────────
  const handleSaveFieldMapping = async () => {
    setSavingMapping(true);
    try {
      // Save by piggy-backing on the /setup PUT (which now accepts field_mapping)
      const body = { business_id: businessId, field_mapping: fieldMapping };
      // PUT setup is partial — it preserves unchanged secrets. We still need
      // to echo back required fields it validates against (page_id stays).
      body.page_id = pageId.trim() || setup?.page_id || "";
      body.page_name = pageName.trim() || setup?.page_name || "";
      body.form_ids = formIdsCsv.split(",").map(s => s.trim()).filter(Boolean);
      body.enabled = enabled;
      body.auto_assign_enabled = autoAssign;
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/setup`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (r.ok) { toast.success("Field mapping saved"); await fetchSetup(); }
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Save failed"); }
    } catch (e) { toast.error("Save failed"); } finally { setSavingMapping(false); }
  };

  const addMappingRow = () => {
    let key = "custom_field";
    let i = 1;
    while (fieldMapping[key] !== undefined) key = `custom_field_${i++}`;
    setFieldMapping((m) => ({ ...m, [key]: [""] }));
  };
  const updateMappingKey = (oldKey, newKey) => {
    if (!newKey || oldKey === newKey) return;
    setFieldMapping((m) => {
      const out = { ...m };
      out[newKey] = out[oldKey];
      delete out[oldKey];
      return out;
    });
  };
  const updateMappingSources = (key, csv) => {
    setFieldMapping((m) => ({ ...m, [key]: csv.split(",").map(s => s.trim()).filter(Boolean) }));
  };
  const removeMappingRow = (key) => {
    setFieldMapping((m) => { const out = { ...m }; delete out[key]; return out; });
  };
  const restoreDefaultMapping = () => setFieldMapping(defaultMapping || {});

  // ──────── Test Payload helpers ────────
  const sampleTestPayload = JSON.stringify({
    id: "leadgen_demo_001",
    created_time: new Date().toISOString(),
    form_id: "9999999999",
    form_name: "Demo Form",
    field_data: [
      { name: "full_name",                          values: ["Rahul Sharma"] },
      { name: "email",                              values: ["rahul@example.com"] },
      { name: "phone_number",                       values: ["+91 9876543210"] },
      { name: "what_course_are_you_interested_in",  values: ["MBBS 2026"] },
      { name: "city",                               values: ["Bareilly"] },
      { name: "parents_name",                       values: ["Sunita Sharma"] },
    ],
  }, null, 2);

  const runTestPayload = async (opts = {}) => {
    setTestPayloadRunning(true);
    setTestPayloadResult(null);
    try {
      const body = { business_id: businessId };
      // Always pass the DRAFT mapping (so unsaved changes preview correctly).
      // Empty draft means "use saved" — but if defaults preview, we send those.
      const draft = Object.keys(fieldMapping).length > 0 ? fieldMapping : (defaultMapping || {});
      body.field_mapping = draft;
      if (opts.fetchLatestFromFormId) {
        body.fetch_latest_from_form_id = opts.fetchLatestFromFormId;
      } else if (opts.leadgenId) {
        body.leadgen_id = opts.leadgenId;
      } else {
        // Use pasted/sample JSON
        if (!testPayloadInput.trim()) { toast.error("Paste a JSON payload first"); setTestPayloadRunning(false); return; }
        try { body.raw_payload = JSON.parse(testPayloadInput); }
        catch (e) { toast.error("Invalid JSON: " + e.message); setTestPayloadRunning(false); return; }
      }
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/test-payload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setTestPayloadResult(d);
        // If we fetched, also load the raw into the textarea so the admin can tweak/re-run
        if (opts.fetchLatestFromFormId || opts.leadgenId) {
          try {
            const fdLike = {
              id: d.preview_lead?.fb_lead_id || opts.leadgenId,
              form_id: d.preview_lead?.fb_form_id,
              field_data: Object.entries(d.raw_fields || {}).map(([k, v]) => ({ name: k, values: [v] })),
            };
            setTestPayloadInput(JSON.stringify(fdLike, null, 2));
          } catch (_) {}
        }
        toast.success(`Dry-run OK — ${Object.keys(d.mapped_fields || {}).length} field(s) mapped, ${(d.unmapped_fb_keys || []).length} unmapped`);
      } else {
        setTestPayloadResult({ error: d.detail || "Test failed", response: d.response });
        toast.error(d.detail || "Test failed");
      }
    } catch (e) {
      setTestPayloadResult({ error: e.message });
      toast.error("Test failed");
    } finally { setTestPayloadRunning(false); }
  };

  // Map an unmapped FB key into the current draft mapping at the chosen CRM field
  const mapUnmappedKey = (fbKey, crmFieldHint) => {
    const existing = Object.keys(fieldMapping).length > 0 ? { ...fieldMapping } : { ...defaultMapping };
    const target = window.prompt(
      `Map "${fbKey}" → which CRM field?\n\nExisting CRM fields: ${Object.keys(existing).join(", ") || "(none)"}\n\nType an existing name or a new one:`,
      crmFieldHint || fbKey
    );
    if (!target) return;
    const k = target.toLowerCase().replace(/\s+/g, "_");
    const prev = existing[k];
    const arr = Array.isArray(prev) ? prev.slice() : (prev ? [prev] : []);
    if (!arr.includes(fbKey)) arr.push(fbKey);
    const next = { ...existing, [k]: arr };
    setFieldMapping(next);
    toast.success(`"${fbKey}" added to ${k} — re-run test to preview`);
  };

  const openQuestionsDialog = async (formId) => {
    if (!formId) { toast.error("Pick a form ID first"); return; }
    setQuestionsDialog({ open: true, formId, loading: true, data: null });
    try {
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/form-questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ business_id: businessId, form_id: formId }),
      });
      const d = await r.json().catch(() => ({}));
      setQuestionsDialog({ open: true, formId, loading: false, data: { ok: r.ok, ...d } });
    } catch (e) {
      setQuestionsDialog({ open: true, formId, loading: false, data: { ok: false, error: e.message } });
    }
  };

  const handleSyncHistorical = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/integrations/facebook/sync-historical`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ business_id: businessId, days: Number(syncDays) }),
      });
      const d = await r.json().catch(() => ({}));
      setSyncResult({ ok: r.ok, data: d });
      if (r.ok) {
        toast.success(`Queued ${d.enqueued} lead(s) from last ${d.days} day(s)`);
        fetchLogs();
      } else toast.error(d.detail || "Sync failed");
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const copy = (txt, label) => {
    navigator.clipboard.writeText(txt);
    toast.success(`${label} copied`);
  };

  const webhookUrl = setup?.webhook_url
    || `${BACKEND_URL || ""}${FALLBACK_WEBHOOK_PATH}`;
  const verifyToken = setup?.verify_token || "(saved after first save)";

  if (loading) {
    return (
      <Card><CardContent className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" /></CardContent></Card>
    );
  }

  const isConfigured = setup?.page_access_token_present && setup?.app_secret_present && setup?.page_id;
  const isLive = isConfigured && setup?.enabled;

  const tokenExpired = setup?.token_status === "expired";

  return (
    <>
      {/* Token-expired warning — top of page so it's impossible to miss */}
      {tokenExpired && (
        <Card className="mb-4 border-2 border-rose-300 bg-rose-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-rose-100 rounded-lg shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-rose-900 mb-1">Facebook Page Access Token expired — leads are not syncing</p>
                <p className="text-sm text-rose-800 mb-2">
                  {setup?.token_error_msg || "The saved token can no longer access Graph API."}
                  {setup?.token_error_at && (
                    <span className="text-rose-600 ml-2 text-xs">
                      (detected {new Date(setup.token_error_at).toLocaleString()})
                    </span>
                  )}
                </p>
                <p className="text-sm text-rose-700">
                  <strong>Fix:</strong> click <em>Connect with Facebook</em> below to regenerate a fresh 60-day long-lived token. The page binding, field mapping, and historical data all stay intact.
                </p>
              </div>
              {appConfig?.configured && (
                <Button
                  onClick={handleStartOAuth}
                  disabled={oauthInFlight}
                  className="bg-rose-600 hover:bg-rose-700 shrink-0"
                >
                  {oauthInFlight ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Facebook className="w-4 h-4 mr-1" />}
                  Reconnect
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex gap-3 items-start justify-between">
            <div className="flex gap-3 items-start">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl shadow-lg">
                <Facebook className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl flex items-center gap-2">
                  Facebook Lead Ads
                  {tokenExpired
                    ? <Badge className="bg-rose-100 text-rose-700 border border-rose-200">Token expired</Badge>
                    : isLive
                      ? <Badge className="bg-green-100 text-green-700 border border-green-200">Live</Badge>
                      : isConfigured
                        ? <Badge variant="outline" className="text-amber-700 border-amber-300">Configured · Paused</Badge>
                        : <Badge variant="outline" className="text-slate-500">Not configured</Badge>
                  }
                </CardTitle>
                <CardDescription className="mt-1 max-w-2xl">
                  Capture leads from your Facebook Lead Ad forms automatically. Meta webhook → CRM lead in 5-10 seconds.
                </CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.open("https://developers.facebook.com/docs/marketing-api/guides/lead-ads/quickstart", "_blank")}>
              <ExternalLink className="w-4 h-4 mr-1" /> Meta docs
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* ════════ NEW: One-click OAuth flow ════════ */}
      <Card className="mb-6 border-2 border-blue-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <LogIn className="w-5 h-5 text-blue-600" />
            Connect with Facebook (recommended)
            {appConfig?.configured
              ? <Badge className="bg-green-100 text-green-700 border border-green-200">App ready</Badge>
              : <Badge variant="outline" className="text-amber-700 border-amber-300">App not configured</Badge>}
          </CardTitle>
          <CardDescription>
            Login with Facebook, pick a Page, click Connect. No tokens to paste.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Currently connected page — shown when an integration row exists */}
          {setup?.page_id && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-green-600 flex items-center justify-center shrink-0">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-green-900 truncate">Connected: {setup.page_name || setup.page_id}</p>
                  <p className="text-xs text-green-700 font-mono truncate">Page ID {setup.page_id} · {setup.enabled ? "active" : "paused"}</p>
                  <p className="text-[11px] text-green-700 mt-0.5">
                    {setup.last_lead_received_at
                      ? <>Last lead: <strong>{new Date(setup.last_lead_received_at).toLocaleString()}</strong></>
                      : <>No leads received yet</>}
                    {setup.last_poll_at && (
                      <> · Last poll: {new Date(setup.last_poll_at).toLocaleString()}{setup.last_poll_enqueued != null ? ` (+${setup.last_poll_enqueued} new)` : ""}</>
                    )}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="fb-disconnect-btn"
              >
                {disconnecting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                Disconnect
              </Button>
            </div>
          )}

          {/* Step A: App credentials (one-time admin setup) */}
          {!appConfig?.configured && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold mb-1">One-time setup</p>
                  <p>
                    Register a Facebook App at <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="underline">developers.facebook.com</a> (type: Business),
                    add the <em>Facebook Login</em> product, then add this redirect URI to it:
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="flex-1 p-2 bg-white border border-amber-200 rounded text-xs font-mono break-all">
                      {appConfig?.callback_url || `${BACKEND_URL}/api/integrations/facebook/oauth-callback`}
                    </code>
                    <Button size="sm" variant="outline" onClick={() => copy(appConfig?.callback_url || `${BACKEND_URL}/api/integrations/facebook/oauth-callback`, "Redirect URI")}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <p className="mt-2 text-xs">Then paste the App ID and Secret below.</p>
                </div>
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">App ID</Label>
              <Input
                value={appIdInput}
                onChange={(e) => setAppIdInput(e.target.value)}
                placeholder="e.g. 8123456789012345"
                className="mt-1 font-mono"
                data-testid="fb-app-id"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">App Secret {appConfig?.app_secret_present ? "(saved)" : ""}</Label>
              <div className="mt-1 relative">
                <Input
                  type={showAppSecret ? "text" : "password"}
                  value={appSecretInput}
                  onChange={(e) => setAppSecretInput(e.target.value)}
                  placeholder={appConfig?.app_secret_present ? `Stored — ${appConfig.app_secret_preview}` : "Paste App Secret"}
                  className="font-mono pr-9"
                  data-testid="fb-app-secret-oauth"
                />
                <button type="button" onClick={() => setShowAppSecret(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showAppSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <Button variant="outline" size="sm" onClick={handleSaveAppCfg} disabled={savingAppCfg}>
              {savingAppCfg ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
              Save App credentials
            </Button>
            <Button
              onClick={handleStartOAuth}
              disabled={!appConfig?.configured || oauthInFlight}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="fb-connect-oauth"
            >
              {oauthInFlight ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Facebook className="w-4 h-4 mr-1" />}
              Connect with Facebook
            </Button>
          </div>

          {/* Post-OAuth: page picker */}
          {oauthError === "fb_access_blocked" ? (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900 space-y-2" data-testid="fb-access-blocked-panel">
              <div className="font-bold flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Facebook blocked the login — "App not active"
              </div>
              <p>
                The Facebook window closed without completing. This happens when the Meta app is in
                <b> Development Mode</b> and the Facebook account you logged in with doesn't have a
                role on the app. It is <b>not</b> a problem with this CRM or your page.
              </p>
              <div className="font-semibold mt-1">Fix — either one works:</div>
              <ol className="list-decimal ml-5 space-y-1">
                <li>
                  <b>Use an authorised Facebook account.</b> Ask your CRM administrator which Facebook
                  login is registered on the Meta app, make that account an admin of your Facebook page
                  (Page → Settings → Page access), and retry Connect from that login.
                </li>
                <li>
                  <b>Get your account authorised.</b> Ask the administrator to add your Facebook profile
                  as a Tester on the Meta app (developers.facebook.com → App Roles), accept the invite
                  from your Facebook notifications, then retry.
                </li>
              </ol>
              <p className="text-amber-700 text-[12.5px]">
                Permanent fix for all businesses: the Meta app needs App Review approval (Live mode) —
                your administrator can submit it from the Meta developer dashboard.
              </p>
            </div>
          ) : oauthError && (
            <div className="p-3 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800 flex items-start gap-2">
              <XCircle className="w-4 h-4 mt-0.5" /> {oauthError}
            </div>
          )}
          {oauthPages && oauthPages.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-slate-50 border-b px-3 py-2 text-sm font-medium text-slate-700 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-emerald-600" />
                Your Facebook Pages — pick one to connect
              </div>
              <ul className="divide-y">
                {oauthPages.map((p) => (
                  <li key={p.id} className="p-3 flex items-center justify-between gap-3 hover:bg-slate-50">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{p.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{p.id}{p.category ? ` · ${p.category}` : ""}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleConnectPage(p)}
                      disabled={connectingPageId === p.id}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {connectingPageId === p.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                      Connect
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {oauthPages && oauthPages.length === 0 && (
            <div className="p-3 rounded bg-amber-50 border border-amber-200 text-sm text-amber-800">
              No pages found on this Facebook account. Make sure your user is an admin on at least one Page.
            </div>
          )}
        </CardContent>
      </Card>

      {/* One-time setup checklist */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2"><BookOpen className="w-5 h-5 text-blue-600" /> Setup checklist</CardTitle>
          <CardDescription>Complete these in Meta first, then fill the form below.</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="text-sm text-slate-700 space-y-2 list-decimal list-inside">
            <li>Create a <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="text-blue-600 underline">Facebook App</a> (type: Business). Note the App Secret.</li>
            <li>Add the <em>Webhooks</em> product to the app, subscribe to the <code className="bg-slate-100 px-1 rounded">page</code> object on the <code className="bg-slate-100 px-1 rounded">leadgen</code> field.</li>
            <li>Add this app to your Business Manager and request <code className="bg-slate-100 px-1 rounded">leads_retrieval</code> permission (App Review may be required for production traffic).</li>
            <li>Generate a <strong>Page Access Token</strong> with <code className="bg-slate-100 px-1 rounded">pages_manage_metadata</code> + <code className="bg-slate-100 px-1 rounded">leads_retrieval</code>. Convert to long-lived if possible.</li>
            <li>In Meta Developer Console → Webhooks, paste the <strong>Callback URL</strong> and <strong>Verify Token</strong> shown below. Click <em>Verify and Save</em>.</li>
            <li>Subscribe your Page (Page Settings → Advanced Messaging → subscribe app).</li>
          </ol>
        </CardContent>
      </Card>

      {/* Webhook URL + verify token */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2"><Zap className="w-5 h-5 text-emerald-600" /> Webhook endpoint</CardTitle>
          <CardDescription>Paste these into Meta Developer Console → Webhooks → Edit subscription.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Callback URL</Label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 p-2.5 bg-slate-50 border rounded text-sm font-mono break-all">{webhookUrl}</code>
              <Button size="sm" variant="outline" onClick={() => copy(webhookUrl, "URL")}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Verify Token</Label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 p-2.5 bg-slate-50 border rounded text-sm font-mono break-all">{verifyToken}</code>
              <Button size="sm" variant="outline" disabled={!setup?.verify_token} onClick={() => copy(verifyToken, "Token")}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
            {!setup?.verify_token && (
              <p className="text-xs text-slate-500 mt-1">A verify token is auto-generated on first save.</p>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Subscribe to</Label>
            <div className="flex items-center gap-2 mt-1">
              <Badge className="bg-blue-100 text-blue-700">page → leadgen</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Setup form — advanced manual flow (collapsible) */}
      <Card className="mb-6">
        <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setShowAdvanced((v) => !v)}>
          <CardTitle className="text-lg flex items-center gap-2">
            {showAdvanced ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
            <Settings2 className="w-5 h-5 text-slate-600" />
            Manual setup (advanced)
            <Badge variant="outline" className="text-xs text-slate-500 ml-2">Optional fallback</Badge>
          </CardTitle>
          <CardDescription>
            Paste Page ID, Page Access Token, App Secret, Form IDs by hand. Use this if OAuth isn't available.
          </CardDescription>
        </CardHeader>
        {showAdvanced && (
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Page ID *</Label>
              <Input value={pageId} onChange={e => setPageId(e.target.value)} placeholder="e.g. 1024357xxxxxxxx" className="mt-1 font-mono" data-testid="fb-page-id" />
              <p className="text-xs text-slate-500 mt-1">Find in Page → About → Page transparency.</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Page name (optional)</Label>
              <Input value={pageName} onChange={e => setPageName(e.target.value)} placeholder="Display label" className="mt-1" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Page Access Token {setup?.page_access_token_present ? "(saved)" : "*"}</Label>
              <div className="mt-1 relative">
                <Input
                  type={showToken ? "text" : "password"}
                  value={pageAccessToken}
                  onChange={e => setPageAccessToken(e.target.value)}
                  placeholder={setup?.page_access_token_present ? `Stored — ${setup.page_access_token_preview}` : "Paste Page Access Token"}
                  className="font-mono pr-9"
                  data-testid="fb-page-token"
                />
                <button type="button" onClick={() => setShowToken(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Leave blank to keep saved token.</p>
            </div>
            <div>
              <Label className="text-sm font-medium">App Secret {setup?.app_secret_present ? "(saved)" : "*"}</Label>
              <div className="mt-1 relative">
                <Input
                  type={showSecret ? "text" : "password"}
                  value={appSecret}
                  onChange={e => setAppSecret(e.target.value)}
                  placeholder={setup?.app_secret_present ? `Stored — ${setup.app_secret_preview}` : "Paste App Secret"}
                  className="font-mono pr-9"
                  data-testid="fb-app-secret"
                />
                <button type="button" onClick={() => setShowSecret(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Used to verify the X-Hub-Signature-256 header on inbound webhooks.</p>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Form IDs (comma-separated, optional)</Label>
            <Input value={formIdsCsv} onChange={e => setFormIdsCsv(e.target.value)} placeholder="e.g. 9876543210, 1234567890" className="mt-1 font-mono" data-testid="fb-form-ids" />
            <p className="text-xs text-slate-500 mt-1">Leave blank to accept leads from ALL forms on this page.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <label className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-100 rounded-lg cursor-pointer">
              <div>
                <p className="text-sm font-medium text-emerald-900">Enabled</p>
                <p className="text-xs text-emerald-700">Master switch — when OFF, the webhook accepts events but does not create leads.</p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="fb-enabled-switch" />
            </label>
            <label className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg cursor-pointer">
              <div>
                <p className="text-sm font-medium text-blue-900">Auto-assign new leads</p>
                <p className="text-xs text-blue-700">Use existing lead-assignment rules (course/category) to route leads automatically.</p>
              </div>
              <Switch checked={autoAssign} onCheckedChange={setAutoAssign} />
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={fetchSetup} disabled={saving}>Reset</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
              Save configuration
            </Button>
          </div>
        </CardContent>
        )}
      </Card>

      {/* ════════ Field Mapping ════════ */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-violet-600" /> Field Mapping
            <Badge variant="outline" className="text-xs text-slate-500 ml-2">
              {Object.keys(fieldMapping || {}).length || Object.keys(defaultMapping || {}).length} rule(s)
            </Badge>
          </CardTitle>
          <CardDescription>
            Map your Facebook lead form questions to CRM fields. Add multiple FB names per CRM field as a fallback chain — the first non-empty match wins.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Header row */}
          <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-600 px-1">
            <span className="col-span-3">CRM field</span>
            <span className="col-span-8">Facebook question name(s) — comma-separated</span>
            <span className="col-span-1"></span>
          </div>

          {/* Either user-defined rows or fall back to a preview of defaults */}
          {(Object.keys(fieldMapping).length === 0 ? defaultMapping : fieldMapping) &&
            Object.entries(Object.keys(fieldMapping).length === 0 ? defaultMapping : fieldMapping).map(([crmField, sources]) => {
              const editable = Object.keys(fieldMapping).length > 0;
              const csv = Array.isArray(sources) ? sources.join(", ") : String(sources || "");
              return (
                <div key={crmField} className="grid grid-cols-12 gap-2 items-center">
                  <Input
                    value={crmField}
                    onChange={(e) => editable && updateMappingKey(crmField, e.target.value.toLowerCase().replace(/\s+/g, "_"))}
                    className="col-span-3 h-9 text-sm font-mono"
                    disabled={!editable}
                  />
                  <Input
                    value={csv}
                    onChange={(e) => editable ? updateMappingSources(crmField, e.target.value)
                      : setFieldMapping({ ...defaultMapping, [crmField]: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                    placeholder="e.g. full_name, applicant_full_name"
                    className="col-span-8 h-9 text-sm font-mono"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    className="col-span-1 h-9 w-9 p-0 text-rose-500"
                    onClick={() => editable ? removeMappingRow(crmField) : null}
                    disabled={!editable}
                    title="Remove row"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                  </Button>
                </div>
              );
            })}

          {Object.keys(fieldMapping).length === 0 && (
            <div className="text-xs text-slate-500 italic pl-1">
              Showing default mapping (read-only preview). Click "Edit" below to customise.
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-3 border-t">
            {Object.keys(fieldMapping).length === 0 ? (
              <Button size="sm" variant="outline" onClick={() => setFieldMapping(defaultMapping || {})}>
                <Settings2 className="w-3.5 h-3.5 mr-1" /> Edit mapping
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={addMappingRow}>
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Add CRM field
                </Button>
                <Button size="sm" variant="outline" onClick={restoreDefaultMapping}>
                  <RotateCw className="w-3.5 h-3.5 mr-1" /> Restore defaults
                </Button>
              </>
            )}
            {/* Discover form fields: prompts admin for a form_id, fetches from Graph API */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const candidate = (formIdsCsv.split(",")[0] || "").trim();
                const formId = window.prompt("Enter a Facebook Form ID to fetch its questions", candidate || "");
                if (formId) openQuestionsDialog(formId.trim());
              }}
              disabled={!setup?.page_access_token_present}
            >
              <BookOpen className="w-3.5 h-3.5 mr-1" /> Discover form fields
            </Button>
            {/* Send Test Payload — paste/fetch a real lead JSON and dry-run the current mapping */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setTestPayloadDialog({ open: true });
                if (!testPayloadInput.trim()) setTestPayloadInput(sampleTestPayload);
                setTestPayloadFormId((formIdsCsv.split(",")[0] || "").trim());
              }}
              data-testid="fb-send-test-payload"
            >
              <Send className="w-3.5 h-3.5 mr-1" /> Send Test Payload
            </Button>
            <Button
              size="sm"
              className="bg-violet-600 hover:bg-violet-700 ml-auto"
              onClick={handleSaveFieldMapping}
              disabled={savingMapping || Object.keys(fieldMapping).length === 0}
            >
              {savingMapping ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
              Save mapping
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Discover form fields dialog */}
      {questionsDialog.open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setQuestionsDialog({ open: false, formId: "", loading: false, data: null })}
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-blue-600" /> Form questions — {questionsDialog.formId}
              </h3>
              <button onClick={() => setQuestionsDialog({ open: false, formId: "", loading: false, data: null })} className="text-slate-400 hover:text-slate-700">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {questionsDialog.loading ? (
                <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-600" /></div>
              ) : !questionsDialog.data?.ok ? (
                <div className="p-3 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800">
                  <p className="font-semibold mb-1">Failed to load questions</p>
                  <pre className="text-[10px] overflow-auto">{JSON.stringify(questionsDialog.data?.response || questionsDialog.data?.detail || questionsDialog.data?.error || {}, null, 2)}</pre>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-600 mb-3">
                    <strong>{questionsDialog.data.form?.name || "—"}</strong> · {questionsDialog.data.questions?.length || 0} question(s)
                  </p>
                  {(questionsDialog.data.questions || []).length === 0 ? (
                    <div className="text-sm text-slate-500 text-center py-6">No questions on this form.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2 font-semibold text-xs">FB question key</th>
                          <th className="text-left p-2 font-semibold text-xs">Label</th>
                          <th className="text-left p-2 font-semibold text-xs">Type</th>
                          <th className="text-right p-2 font-semibold text-xs">Use</th>
                        </tr>
                      </thead>
                      <tbody>
                        {questionsDialog.data.questions.map((q) => (
                          <tr key={q.key} className="border-t hover:bg-slate-50">
                            <td className="p-2 font-mono text-xs">{q.key}</td>
                            <td className="p-2">{q.label}</td>
                            <td className="p-2 text-slate-500">{q.type}</td>
                            <td className="p-2 text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  // Append this key to the focused CRM field's source list,
                                  // OR insert it as a brand-new row keyed by the key itself.
                                  const ed = Object.keys(fieldMapping).length > 0 ? fieldMapping : { ...defaultMapping };
                                  const targetKey = window.prompt(
                                    `Add "${q.key}" as a source for which CRM field?\n\nExisting CRM fields:\n${Object.keys(ed).join(", ")}\n\nType an existing name or a new one:`,
                                    q.key
                                  );
                                  if (!targetKey) return;
                                  const k = targetKey.toLowerCase().replace(/\s+/g, "_");
                                  const prev = ed[k];
                                  const arr = Array.isArray(prev) ? prev.slice() : (prev ? [prev] : []);
                                  if (!arr.includes(q.key)) arr.push(q.key);
                                  setFieldMapping({ ...ed, [k]: arr });
                                  toast.success(`"${q.key}" added to ${k}`);
                                }}
                              >
                                Map →
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════ Send Test Payload Dialog ════════ */}
      {testPayloadDialog.open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setTestPayloadDialog({ open: false })}
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Send className="w-4 h-4 text-violet-600" /> Send Test Payload
              </h3>
              <button onClick={() => setTestPayloadDialog({ open: false })} className="text-slate-400 hover:text-slate-700">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 grid md:grid-cols-2 gap-4">
              {/* LEFT — input */}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase text-slate-500">Lead JSON payload</Label>
                  <textarea
                    value={testPayloadInput}
                    onChange={(e) => setTestPayloadInput(e.target.value)}
                    rows={16}
                    className="mt-1 w-full font-mono text-xs border rounded p-2 focus:outline-none focus:ring-2 focus:ring-violet-500"
                    placeholder='{"field_data":[{"name":"full_name","values":["..."]},...]}'
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Accepts either Meta's <code className="bg-slate-100 px-1 rounded">{`{field_data:[{name,values}]}`}</code> shape OR a flat <code className="bg-slate-100 px-1 rounded">{`{key:value}`}</code> object.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setTestPayloadInput(sampleTestPayload)}>
                    <BookOpen className="w-3.5 h-3.5 mr-1" /> Use sample
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const fid = window.prompt("Fetch the latest lead from this Form ID:", testPayloadFormId || "");
                      if (fid) { setTestPayloadFormId(fid.trim()); runTestPayload({ fetchLatestFromFormId: fid.trim() }); }
                    }}
                    disabled={!setup?.page_access_token_present}
                  >
                    <RotateCw className="w-3.5 h-3.5 mr-1" /> Fetch latest from form
                  </Button>
                  <Button
                    size="sm"
                    className="bg-violet-600 hover:bg-violet-700 ml-auto"
                    onClick={() => runTestPayload()}
                    disabled={testPayloadRunning || !testPayloadInput.trim()}
                  >
                    {testPayloadRunning ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                    Run Test
                  </Button>
                </div>
              </div>

              {/* RIGHT — result */}
              <div className="space-y-3">
                {!testPayloadResult ? (
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center text-slate-400 text-sm">
                    Paste JSON or fetch a real lead, then click <strong>Run Test</strong>.<br />
                    The result will show how each Facebook field resolves to a CRM field with your current mapping.
                  </div>
                ) : testPayloadResult.error ? (
                  <div className="p-3 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800">
                    <p className="font-semibold mb-1">{testPayloadResult.error}</p>
                    {testPayloadResult.response && (
                      <pre className="text-[10px] overflow-auto max-h-48">{JSON.stringify(testPayloadResult.response, null, 2)}</pre>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Mapped */}
                    <div>
                      <p className="text-xs uppercase text-slate-500 flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                        Mapped CRM fields ({Object.keys(testPayloadResult.mapped_fields || {}).length})
                      </p>
                      <div className="mt-1 border rounded">
                        <table className="w-full text-xs">
                          <tbody>
                            {Object.entries(testPayloadResult.mapped_fields || {}).map(([k, v]) => (
                              <tr key={k} className="border-b last:border-b-0">
                                <td className="p-2 font-mono text-violet-700 w-1/3">{k}</td>
                                <td className="p-2 text-slate-700">{String(v)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {Object.keys(testPayloadResult.mapped_fields || {}).length === 0 && (
                          <p className="p-3 text-xs text-slate-500 italic">Nothing mapped yet. Edit mapping or use defaults.</p>
                        )}
                      </div>
                    </div>

                    {/* Unmapped */}
                    {testPayloadResult.unmapped_fb_keys?.length > 0 && (
                      <div>
                        <p className="text-xs uppercase text-slate-500 flex items-center gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                          Unmapped Facebook fields ({testPayloadResult.unmapped_fb_keys.length}) — click "Map" to bind
                        </p>
                        <div className="mt-1 border rounded">
                          <table className="w-full text-xs">
                            <tbody>
                              {testPayloadResult.unmapped_fb_keys.map((k) => {
                                const v = testPayloadResult.raw_fields?.[k];
                                return (
                                  <tr key={k} className="border-b last:border-b-0 hover:bg-slate-50">
                                    <td className="p-2 font-mono text-slate-700 w-1/3">{k}</td>
                                    <td className="p-2 text-slate-500">{String(v || "")}</td>
                                    <td className="p-2 w-20 text-right">
                                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => mapUnmappedKey(k, k)}>
                                        Map →
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Preview lead doc */}
                    <div>
                      <p className="text-xs uppercase text-slate-500 flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-blue-600" />
                        Preview lead document (what would be inserted)
                      </p>
                      <pre className="mt-1 border rounded p-2 bg-slate-50 text-[10px] leading-tight max-h-40 overflow-auto">
                        {JSON.stringify(testPayloadResult.preview_lead, null, 2)}
                      </pre>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t">
                      <span>Source: <code className="bg-slate-100 px-1 rounded">{testPayloadResult.source}</code> · using {testPayloadResult.using_override_mapping ? "draft mapping" : "saved mapping"}</span>
                      <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={() => { handleSaveFieldMapping(); }}>
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Save current mapping
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test + historical sync */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Send className="w-4 h-4 text-blue-600" /> Test connection</CardTitle>
            <CardDescription>Calls Graph API to list lead forms on this page.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleTest} disabled={testing || !setup?.page_access_token_present} className="bg-blue-600 hover:bg-blue-700 w-full">
              {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Test connection
            </Button>
            {testResult && (
              <div className={`mt-3 p-3 rounded text-xs ${testResult.ok ? "bg-green-50 border border-green-200" : "bg-rose-50 border border-rose-200"}`}>
                {testResult.ok
                  ? (<>
                      <div className="flex items-center gap-2 mb-2 font-semibold text-green-700"><CheckCircle className="w-4 h-4" /> Success</div>
                      <p className="text-slate-700 mb-2">{testResult.data?.forms?.length || 0} lead form(s) on this page:</p>
                      <ul className="space-y-1">
                        {(testResult.data?.forms || []).map(f => (
                          <li key={f.id} className="flex items-center justify-between bg-white p-2 rounded border">
                            <span className="font-medium">{f.name}</span>
                            <span className="text-slate-500 font-mono text-[10px]">{f.id}</span>
                          </li>
                        ))}
                      </ul>
                    </>)
                  : (<>
                      <div className="flex items-center gap-2 mb-1 font-semibold text-rose-700"><XCircle className="w-4 h-4" /> Failed</div>
                      <pre className="overflow-auto bg-white/70 p-2 rounded text-[10px] leading-tight max-h-32">{JSON.stringify(testResult.data, null, 2)}</pre>
                    </>)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><RotateCw className="w-4 h-4 text-emerald-600" /> Historical sync</CardTitle>
            <CardDescription>Backfill leads created before webhook was wired.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-2">
              <Select value={syncDays} onValueChange={setSyncDays}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSyncHistorical} disabled={syncing || !setup?.page_access_token_present} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCw className="w-4 h-4 mr-1" />}
                Sync now
              </Button>
            </div>
            {syncResult?.ok && (
              <div className="p-3 rounded text-xs bg-green-50 border border-green-200">
                <p className="font-semibold text-green-700">Enqueued {syncResult.data.enqueued} of {syncResult.data.total_examined} leads</p>
                <p className="text-slate-700 mt-1">{syncResult.data.forms_scanned} form(s) scanned. Watch the activity panel below.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activity */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-violet-600" /> Activity</CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchLogs}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          </div>
          <CardDescription>Pending queue · recent completions · failed jobs · webhook log (last 25).</CardDescription>
        </CardHeader>
        <CardContent>
          {!logs ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
            <div className="space-y-4">
              <ActivityGrid logs={logs} />
              <WebhookLogTable rows={logs.webhook_logs} />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function ActivityGrid({ logs }) {
  const tile = (label, n, accent) => (
    <div className={`p-3 rounded-lg border ${accent}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-2xl font-bold text-slate-800">{n}</p>
    </div>
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {tile("Pending", logs.pending_jobs?.length || 0, "bg-amber-50 border-amber-100")}
      {tile("Completed (recent)", logs.recent_completions?.length || 0, "bg-emerald-50 border-emerald-100")}
      {tile("Failed", logs.failed_jobs?.length || 0, "bg-rose-50 border-rose-100")}
      {tile("Webhook events", logs.webhook_logs?.length || 0, "bg-slate-50 border-slate-200")}
    </div>
  );
}

function WebhookLogTable({ rows = [] }) {
  if (!rows.length) {
    return <div className="text-center py-8 text-slate-500 text-sm border rounded-lg">No webhook events yet. They appear here as Meta delivers them.</div>;
  }
  return (
    <div className="border rounded-lg overflow-auto max-h-72">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left p-2"><Clock className="w-3 h-3 inline-block mr-1" /> When</th>
            <th className="text-left p-2">Kind</th>
            <th className="text-left p-2">Object</th>
            <th className="text-left p-2">Entries</th>
            <th className="text-left p-2">Signature</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.log_id} className="border-t hover:bg-slate-50">
              <td className="p-2 whitespace-nowrap text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
              <td className="p-2 font-mono">{r.kind || "—"}</td>
              <td className="p-2">{r.object || "—"}</td>
              <td className="p-2">{r.entries_count != null ? r.entries_count : "—"}</td>
              <td className="p-2">
                {r.kind === "verify" ? (
                  r.matched ? <Badge className="bg-green-100 text-green-700">verified</Badge>
                            : <Badge className="bg-rose-100 text-rose-700">token mismatch</Badge>
                ) : r.signature_ok ? <Badge className="bg-green-100 text-green-700">ok</Badge>
                                   : <Badge className="bg-rose-100 text-rose-700">invalid sig</Badge>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
